<template>
  <div class="container">
    <loading-spinner v-if="isLoading" :message="this.loadingMessage"></loading-spinner>
    <div class="row table-container" v-if="!isLoading">
      <div v-if="myOfferings.length > 0 && this.apsToken()"">
        <div class="container d-flex flex-wrap justify-content-between align-items-center pt-3 pr-0">
          <div class="help-text pt-3" v-if="this.isStakeholder">
            {{stats}}
            <button title="Reload" class="sync-btn fas fa-sync-alt" @click="pullStats('update')"></button>
          </div>
          <div v-if="!this.isStakeholder" class="help-text">
            Need help? Check the <a href="https://one.autodesk.com/IT/articles/817f4229930952d068233d7efaba100d"
              rel="noopener" target="_blank">FAQ</a> or watch
            <a href="https://share.autodesk.com/sites/One/Shared%20Documents/Videos/Easier/Easier%203.0%20submit%20request.mp4"
              rel="noopener" target="_blank">how to request licenses</a> or <a
              href="https://share.autodesk.com/sites/One/Shared%20Documents/Videos/Easier/Assign%20a%20product%20to%20an%20Autodesk%20Team.mp4"
              rel="noopener" target="_blank">how to assign products to a team</a>
          </div>
        </div>
        <offering-table :offerings="myOfferings" :columns="columns" :onSelectChangeFunc="onSelectChange"
          @handleClick="invokeTransactionOrchestrator" />
      </div>
      <special-terms-modal :needsOxygenAccountOrMFA="needsOxygenAccountOrMFA" v-if="showModal"
        @close="getMyOfferings" />
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/LoadingSpinner";
import SpecialTermsModal from "@/components/SpecialTermsModal";
import util from '@/util';
import { AuthClient as AuthClientSdk } from "@adsk/identity-web-sdk";
import axios from "axios";
import moment from 'moment';
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import api from "../api/index.js";
import OfferingTable from "../components/OfferingTable.vue";
import * as CONSTANTS from "../constants.js";

export default {
  name: "AutodeskSoftware",
  components: {
    SpecialTermsModal,
    LoadingSpinner,
    OfferingTable,
  },
  data: function () {
    return {
      CONSTANTS,
      needsOxygenAccountOrMFA: true,
      selectedProducts: [],
      myOfferings: [],
      stats: '',
      showModal: true,
      loadingMessage: "",
      isLoading: false,
      isStakeholder: false,
      columns: [
        {
          label: "Badge",
          field: "icon",
        },
        {
          label: "Name",
          field: "name",
        },
        {
          label: "Access",
          field: "access",
        },
        {
          label: "Division",
          field: "productDivision",
        },
        {
          label: "Product information",
          field: "productInfoUrl",
        },

      ],
    };
  },
  created() {
    this.validateAutodeskAccount();
    this.checkStakeholderStatus();
  },
  computed: {
    ...mapGetters(["getUser", "getOfferings"]),
  },
  methods: {
    ...mapMutations(["setAPSToken"]),
    ...mapActions(["addAlert"]),
    ...mapState({
      apsToken: (state) => state.token,
    }),
    getMyOfferings() {
      this.showModal = false
      if (this.apsToken() != null) {
        this.myOfferings = this.getOfferings.sort((a, b) => a.productName.localeCompare(b.productName));
        this.myOfferings.forEach((v) =>
          v.vgtDisabled = !!v.subscriptionId
        );
      }
    },
    parseJwt(token) {
      return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
    },
    async onSelectChange(params) {
      await this.validateToken();
      for (let p of params.selectedRows) {
        p.vgtSelected = !p.vgtDisabled;
      }
      this.selectedProducts = params.selectedRows.filter((x) => x.vgtSelected);
    },
    async validateToken() {
      const expire = moment.unix(this.parseJwt(this.apsToken()).exp).subtract(5, 'minutes')
      const now = moment();
      if (now.isSameOrAfter(expire)) {
        await this.validateAutodeskAccount();
        if (!this.apsToken()) {
          this.$router.go()
        }
      }
    },
    async validateAutodeskAccount() {
      this.isLoading = true;
      this.loadingMessage = "Validating Autodesk account";
      try {
        let client = new AuthClientSdk({
          ...CONSTANTS.BASE_APS_OPTIONS,
          authoptions: { ld: CONSTANTS.ADSK_DOMAIN },
        });
        const token = await client.getAccessTokenSilently();
        if (token) {
          this.needsOxygenAccountOrMFA = false;
          this.setAPSToken(token);
        }
      } catch (e) {
        console.log(e);
        this.needsOxygenAccountOrMFA = true;
      }
      this.isLoading = false;
    },
    async invokeTransactionOrchestrator() {
      await this.validateToken();
      this.isLoading = true;
      this.loadingMessage = "Requesting product licenses...";
      const payload = {
        transactionType: CONSTANTS.TRANSACTION_TYPE,
        data: {
          lineItems: this.selectedProducts.map(({ productLineCode }) => ({
            productLineCode,
          })),
        },
      };
      const self = this
      await axios.post(
        process.env.VUE_APP_PELICAN_ORCHESTRATOR_API,
        payload,
        {
          headers: {
            Authorization: "Bearer " + this.apsToken(),
          },
        }
      ).then(async response => {
        const { data: { id: transactionId, status, message } } = response;
        if (transactionId) {
          this.loadingMessage = "Validating requested subscriptions...";
          try {
            await api.postTransaction({
              transactionId,
              status,
              message,
              productLineCodes: payload.data.lineItems.map(
                (x) => x.productLineCode
              ),
            });
            this.trackEvent(
              `transactionId=${transactionId}, status=${status
              }, payload=${JSON.stringify(payload)}`
            );
          } catch (e) {
            console.log('Error while saving transaction in AAD: ', JSON.stringify(e))
          }
          const ws = new WebSocket(process.env.VUE_APP_PELICAN_WSS);
          const wssPayload = { action: CONSTANTS.WSS_ACTION, transactionId };
          ws.onopen = () => ws.send(JSON.stringify(wssPayload));
          ws.addEventListener("message", async (event) => {
            const data = JSON.parse(event.data);
            this.trackEvent(
              `transactionId=${transactionId}, status=${data.status}`
            );
            if (
              data.status === CONSTANTS.WSS_RESPONSE.SUCCESS ||
              data.status === CONSTANTS.WSS_RESPONSE.FAILED_PARTIALLY
            ) {
              for (const detail of data.metaInfo.details) {
                const offering = this.myOfferings.find(
                  (s) => s.productLineCode === detail.productLineExternalKey
                );
                if (detail.status === CONSTANTS.WSS_RESPONSE.SUCCESS) {
                  offering.subscriptionId = detail.subscriptionId;
                  offering.transactionId = transactionId;
                  offering.vgtDisabled = true;
                  offering.isNewlySubscribed = true;
                  offering.expirationDate = moment(new Date(detail.expirationDate)).format("MMM DD, YYYY");
                }
              }
              await api.postTransaction(data);
              ws.close();
            } else if (data.status === CONSTANTS.WSS_RESPONSE.FAILED) {
              await api.postTransaction(data);
              ws.close();
            } else if (data.status === CONSTANTS.WSS_RESPONSE.PROCESSING) {
              this.loadingMessage = "Almost there...";
              await api.postTransaction(data);
            }
          });
          ws.addEventListener("close", async () => {
            this.selectedProducts = []
            this.isLoading = false;
            const subscribed = this.myOfferings.filter(x => x.isNewlySubscribed && x.transactionId === transactionId)
            if (subscribed.length > 0) {
              let text = 'Successfully subscribed to - <br>'
              subscribed.forEach(s => {
                text += `${s.productName} - <a target="_blank" href="${process.env.VUE_APP_MANAGE_REDIRECT}?filter=%7B&quot;offeringPlc&quot;%3A&quot;${s.productLineCode}&quot;%7D">${s.subscriptionId}</a> <br>`
              })
              text += `Note that it may take up to 24 hours sometimes for the new subscriptions to show.`
              this.addAlert(
                util.createAlert(
                  '',
                  text,
                  CONSTANTS.ALERT_CONTEXT.SUCCESS
                )
              );
            }
          });
        }
      }).catch(error => {
        self.isLoading = false;
        self.trackEvent(`error=${JSON.stringify(error)}, payload=${JSON.stringify(payload)}`);
        self.addAlert(
          util.createAlert(
            'Error',
            'An error occurred while processing your request. Please try again later.',
            CONSTANTS.ALERT_CONTEXT.DANGER
          )
        );
      })
    },
    async pullStats(operation) {
      const stats = (operation === 'update' ? await api.refreshStats() : await api.getStats()).data.data
      this.stats = `${stats.totalSubscriptions} licenses requested by ${stats.uniqueUsers} employees over ${stats.uniqueTransactions} transactions in ${stats.pageViews} page views. Data refreshes every 24 hours and was last updated ${moment(stats.created).format('lll')}.`
    },
    async checkStakeholderStatus() {
      if (this.getUser.authorities && this.getUser.authorities.includes(CONSTANTS.EASIER_STAKEHOLDER_ROLE) && process.env.VUE_APP_APS_ENV === 'prd') {
        await this.pullStats()
        this.isStakeholder = true
      } 
      else {
        this.isStakeholder = false
      }
    },
  },
};
</script>
<style scoped>
.table-container {
  margin-top: -40px;
}
.help-text {
  padding-top: 10px;
  padding-bottom: 10px;
  margin-left: -15px;
  font-style: italic;
  font-weight: bolder;
  font-size: 14px;
  font-family: "artifaktElementRegular";
}
.sync-btn {
  border: none;
  background: transparent;
  color: #5f60ff;
  outline: none;
  margin-left: 5px;
  font-size: 14px;
}
.sync-btn:hover {
  color: #0056b3;
}
</style>
